import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Collapse, IconButton, Tooltip, alpha, useTheme,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

function formatDuration(ms) {
  if (!ms || ms < 0) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function PositionRow({ position }) {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const pnl = position.realizedPnlPercent || position.pnlPercent || 0;
  const bestPnl = position.bestPnlPercent || position.highestPnlReached || 0;
  const isClosed = position.status === 'closed' || !!position.closedAt;
  const holdTime = position.closedAt && position.createdAt
    ? new Date(position.closedAt).getTime() - new Date(position.createdAt).getTime()
    : position.createdAt ? Date.now() - new Date(position.createdAt).getTime() : 0;

  const exitReason = position.closeReason || position.exitReason || '—';
  const entryReason = position.entryReason || position.signalMethod || '—';

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', bgcolor: !isClosed ? alpha(theme.palette.info.main, 0.03) : 'inherit' }}
      >
        <TableCell sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" sx={{ p: 0 }}>
              <ExpandMoreIcon sx={{ fontSize: 16, transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
            </IconButton>
            <Typography variant="caption" fontWeight={600}>{position.symbol}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={position.side?.toUpperCase()}
            size="small"
            color={position.side === 'buy' ? 'success' : 'error'}
            sx={{ fontSize: '0.6rem', height: 20, width: 40 }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption">{position.leverage || 5}×</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption">{entryReason.substring(0, 30)}</Typography>
        </TableCell>
        <TableCell>
          {isClosed ? (
            <Typography variant="caption">{exitReason.substring(0, 30)}</Typography>
          ) : (
            <Chip label="Open" size="small" variant="outlined" color="info" sx={{ fontSize: '0.6rem', height: 20 }} />
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="caption" fontWeight={600} color={pnl > 0 ? 'success.main' : pnl < 0 ? 'error.main' : 'text.primary'}>
            {pnl > 0 ? '+' : ''}{pnl.toFixed(2)}%
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="caption" color={bestPnl > pnl + 0.1 ? 'warning.main' : 'text.secondary'}>
            {bestPnl > 0 ? `+${bestPnl.toFixed(2)}%` : '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption">{formatDuration(holdTime)}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {position.trailingActive && <Chip label="Trail" size="small" variant="outlined" color="info" sx={{ fontSize: '0.55rem', height: 18 }} />}
            {position.zeroLockActive && <Chip label="0-Lock" size="small" variant="outlined" color="success" sx={{ fontSize: '0.55rem', height: 18 }} />}
            {position.partialTpExecuted && <Chip label="TP" size="small" variant="outlined" color="warning" sx={{ fontSize: '0.55rem', height: 18 }} />}
            {position.rescueActive && <Chip label="Rescue" size="small" variant="outlined" color="error" sx={{ fontSize: '0.55rem', height: 18 }} />}
          </Box>
        </TableCell>
      </TableRow>

      {/* Expanded Details */}
      {expanded && (
        <TableRow>
          <TableCell colSpan={9} sx={{ py: 1.5, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', pl: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Entry Price</Typography>
                <Typography variant="body2">{position.entryPrice?.toFixed(4) || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current/Exit Price</Typography>
                <Typography variant="body2">{(position.exitPrice || position.currentPrice)?.toFixed(4) || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Stop Loss</Typography>
                <Typography variant="body2" color="error.main">{position.dynamicStopLoss || position.stopLossPercent || '—'}%</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Take Profit</Typography>
                <Typography variant="body2" color="success.main">{position.dynamicTakeProfit || position.takeProfitPercent || '—'}%</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Position Size</Typography>
                <Typography variant="body2">${position.notionalValue?.toFixed(2) || position.positionSizeUsd || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Intent</Typography>
                <Typography variant="body2">{position.positionIntent || '—'}</Typography>
              </Box>
            </Box>

            {/* Trailing Adjustments */}
            {position.trailingHistory && position.trailingHistory.length > 0 && (
              <Box sx={{ mt: 1.5, pl: 3 }}>
                <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Trailing Adjustments:</Typography>
                {position.trailingHistory.slice(-5).map((th, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" display="block">
                    • {new Date(th.timestamp).toLocaleTimeString()}: {th.type} — {th.description || `SL: ${th.newSL?.toFixed(3)}%`}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Multi-Target TP State */}
            {position.multiTargetTPHit && Object.keys(position.multiTargetTPHit).length > 0 && (
              <Box sx={{ mt: 1, pl: 3 }}>
                <Typography variant="caption" fontWeight={600}>Multi-TP Targets Hit: </Typography>
                {Object.entries(position.multiTargetTPHit).filter(([,v]) => v).map(([k]) => (
                  <Chip key={k} label={k} size="small" color="success" variant="outlined" sx={{ mr: 0.5, fontSize: '0.6rem', height: 18 }} />
                ))}
              </Box>
            )}

            {/* Close Reason Detail */}
            {isClosed && position.closeReason && (
              <Box sx={{ mt: 1, pl: 3 }}>
                <Typography variant="caption" fontWeight={600}>Exit Detail: </Typography>
                <Typography variant="caption" color="text.secondary">{position.closeReason}</Typography>
              </Box>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function PositionsTab({ strategyId, positionsData, onRefresh }) {
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    const list = positionsData || [];
    if (filter === 'open') return list.filter(p => !p.closedAt && p.status !== 'closed');
    if (filter === 'closed') return list.filter(p => p.closedAt || p.status === 'closed');
    return list;
  }, [positionsData, filter]);

  const sorted = useMemo(() =>
    [...filtered].sort((a, b) => {
      const aTime = new Date(a.closedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.closedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    }),
    [filtered]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small">
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All ({(positionsData || []).length})</ToggleButton>
          <ToggleButton value="open" sx={{ textTransform: 'none', px: 2 }}>Open ({(positionsData || []).filter(p => !p.closedAt && p.status !== 'closed').length})</ToggleButton>
          <ToggleButton value="closed" sx={{ textTransform: 'none', px: 2 }}>Closed ({(positionsData || []).filter(p => p.closedAt || p.status === 'closed').length})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Symbol</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Side</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Lev</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Entry Reason</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Exit Reason</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>P&L</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Best</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Hold</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No positions found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((pos, i) => <PositionRow key={pos.id || i} position={pos} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
